<template>
    <div>
        <card-header title="New Registration" icon="fa-plus"/>

        <card-body>
            <card-list>
                <!-- Participants -->
                <subheader-list-item icon="fa-users" title="Participants"/>
                <container-list-item padded>
                    <div class="columns">
                        <div class="column">
                            <trip-select label="Trip" v-model="newReg.trip_id"/>
                        </div>
                    </div>
                    <div class="columns">
                        <div class="column">
                            <user-select label="Primary Customer" v-model="newReg.user_id"/>
                        </div>
                    </div>
                    <div class="columns">
                        <div class="column">
                            <b-field label="Primary Type" label-position="inside">
                                <b-select v-model="newReg.type" expanded>
                                    <option value="participant">Participant</option>
                                    <option value="coach">Coach</option>
                                    <option value="staff">Staff</option>
                                    <option value="waitlist">Wait List</option>
                                    <option value="cancelled">Cancelled</option>
                                </b-select>
                            </b-field>
                        </div>
                    </div>
                    <div class="columns">
                        <div class="column">
                            <user-select label="Second Customer (Optional)" v-model="newReg.second_id"/>
                        </div>
                    </div>
                    <div class="columns" v-if="newReg.second_id">
                        <div class="column">
                            <b-field label="Second Type" label-position="inside">
                                <b-select v-model="newReg.second_type" expanded>
                                    <option value="participant">Participant</option>
                                    <option value="coach">Coach</option>
                                    <option value="staff">Staff</option>
                                    <option value="waitlist">Wait List</option>
                                    <option value="cancelled">Cancelled</option>
                                </b-select>
                            </b-field>
                        </div>
                        <div class="column is-flex is-align-items-center">
                            <b-switch v-model="newReg.secondPaying">Second traveler is paying (double-occupancy pricing)</b-switch>
                        </div>
                    </div>
                </container-list-item>

                <!-- Add-ons (participant/wait-list registrations only) -->
                <template v-if="priced">
                    <subheader-list-item icon="fa-layer-plus" title="Add-Ons"/>
                    <container-list-item padded>
                        <b-loading :is-full-page="false" :active="loadingAddons"/>
                        <div class="columns" v-if="departures.length">
                            <div class="column">
                                <b-field label="Departure City" label-position="inside">
                                    <b-select expanded v-model="newReg.departureId">
                                        <option :value="null">- - -</option>
                                        <option :value="d.id" v-for="d in departures" :key="d.id">
                                            {{ d.title }} ({{ (newReg.secondPaying ? d.double_cost : d.single_cost) | currency }})
                                        </option>
                                    </b-select>
                                </b-field>
                            </div>
                        </div>

                        <div v-for="group in addonGroups" :key="group.type" class="mb-3">
                            <p class="has-text-weight-semibold is-size-7 has-text-grey mb-1">{{ group.label }}</p>
                            <div v-for="a in group.items" :key="a.id" class="addon-row">
                                <b-checkbox :native-value="a.id" v-model="selectedAddonIds">
                                    {{ a.title }}
                                    <span class="has-text-grey">— {{ (newReg.secondPaying ? a.double_cost : a.single_cost) | currency }}</span>
                                </b-checkbox>
                            </div>
                        </div>
                        <p v-if="!departures.length && !addonGroups.length && !loadingAddons" class="has-text-grey is-size-7">
                            This trip has no add-ons.
                        </p>
                    </container-list-item>

                    <!-- Payment plan -->
                    <subheader-list-item icon="fa-credit-card-front" title="Payment Plan"/>
                    <container-list-item padded>
                        <div v-if="isWaitlist" class="notification is-warning is-light py-2 px-3 is-size-7 mb-3">
                            <i class="fas fa-exclamation-circle mr-2"></i>Wait-list registration: a deposit-only plan is created. Installments are set later, once the registration is cleared.
                        </div>
                        <b-field v-else label="Plan" label-position="inside">
                            <b-select expanded v-model="newReg.payment">
                                <option value="monthly">Monthly Payment Plan</option>
                                <option value="entire">Pay in Full</option>
                                <option value="none">No Payment Plan (billed on another trip)</option>
                            </b-select>
                        </b-field>

                        <div class="summary mt-3">
                            <div class="summary-row"><span>Program Total</span><strong>{{ pricing.total | currency }}</strong></div>
                            <template v-if="!noPlan">
                                <div class="summary-row"><span>{{ isWaitlist ? 'Wait-list Deposit' : 'Deposit Due Today' }}</span><strong>{{ pricing.deposit | currency }}</strong></div>
                                <div class="summary-row" v-if="showInstallments">
                                    <span>Then {{ pricing.months }} monthly installment{{ pricing.months === 1 ? '' : 's' }}</span>
                                    <strong>{{ pricing.installment | currency }}<span class="has-text-grey">/mo</span></strong>
                                </div>
                            </template>
                            <div class="summary-row" v-else>
                                <span>Billed here</span><strong class="has-text-grey">Nothing</strong>
                            </div>
                        </div>

                        <div v-if="noPlan" class="notification is-warning is-light py-2 px-3 is-size-7 mt-3 mb-0">
                            <i class="fas fa-exclamation-circle mr-2"></i>No payment plan will be created. Use this when the customer pays for this registration
                            somewhere else — typically an extension booked as its own trip and paid for on the main trip.
                            The total above is recorded on the registration for reference only: nothing is scheduled, no card can be
                            stored here, and this registration will <strong>not</strong> appear in Billing &rarr; Due.
                        </div>
                        <template v-else>
                            <div class="notification is-info is-light py-2 px-3 is-size-7 mt-3 mb-0">
                                <i class="fas fa-info-circle mr-2"></i>No card is charged now. After creating, collect the deposit with <strong>Make Payment</strong> (card or check) on the new plan.
                            </div>
                            <b-field class="mt-3">
                                <b-switch v-model="chargeNow" :disabled="pricing.deposit <= 0">Take me to Make Payment after creating</b-switch>
                            </b-field>
                        </template>
                    </container-list-item>
                </template>
            </card-list>
        </card-body>

        <card-footer class="pt-3 pb-3 has-text-right">
            <b-button type="is-primary" size="is-small" @click="submit" :disabled="!canSubmit">
                <i class="fas fa-plus mr-3"></i>Create Registration
            </b-button>
        </card-footer>
    </div>
</template>

<script>
    import CardHeader from '../../TIER/components/CardHeader';
    import CardBody from '../../TIER/components/CardBody';
    import CardFooter from '../../TIER/components/CardFooter';

    import {client as http} from '../../http_client';
    import CardList from "@/TIER/components/CardList";
    import ContainerListItem from "@/TIER/components/ContainerListItem";
    import SubheaderListItem from "../../TIER/components/SubheaderListItem";
    import UserSelect from "../../components/UserSelect";
    import TripSelect from "../../components/TripSelect";

    // Group order + labels mirror the public register flow's Travel Options step.
    const GROUP_ORDER = [
        {type: 'airfare', label: 'Airfare'},
        {type: 'aircredit', label: 'Air Credit'},
        {type: 'accomodations', label: 'Accommodations'},
        {type: 'tennis', label: 'Tennis'},
        {type: 'extensions', label: 'Extensions'},
    ];

    export default {
        props: ['card', 'props'],
        components: {TripSelect, UserSelect, SubheaderListItem, ContainerListItem, CardList, CardFooter, CardBody, CardHeader},
        data: function() {
            return {
                newReg: {
                    trip_id: null,
                    user_id: null,
                    type: 'participant',
                    second_id: null,
                    second_type: 'participant',
                    secondPaying: false,
                    departureId: null,
                    payment: 'monthly',
                },
                trip: null,
                tripAddons: [],
                selectedAddonIds: [],
                loadingAddons: false,
                chargeNow: false,
                submitting: false,
            };
        },
        computed: {
            // Pricing only applies to participant/wait-list registrations; comp types
            // (coach/staff/cancelled) get a bare plan on the server.
            priced() {
                return !!this.newReg.trip_id && (this.newReg.type === 'participant' || this.newReg.type === 'waitlist');
            },
            isWaitlist() {
                return !!(this.trip && this.trip.waitlist);
            },
            // 'none' — create the registration but no payment_plans row at all. For
            // registrations paid for elsewhere (an extension trip billed on the main trip).
            noPlan() {
                return this.newReg.payment === 'none';
            },
            departures() {
                return this.tripAddons.filter(a => a.type === 'departure');
            },
            addonGroups() {
                return GROUP_ORDER
                    .map(g => ({...g, items: this.tripAddons.filter(a => a.type === g.type)}))
                    .filter(g => g.items.length);
            },
            selectedAddons() {
                return this.tripAddons.filter(a => this.selectedAddonIds.indexOf(a.id) >= 0);
            },
            selectedDeparture() {
                return this.departures.find(d => d.id === this.newReg.departureId) || null;
            },
            showInstallments() {
                return !this.isWaitlist && !this.noPlan && this.newReg.payment === 'monthly' && this.pricing.months > 0;
            },
            // Mirrors server computePricing() (§6.2) + buildPlanItems() installment math
            // so staff see exactly what the server will write.
            pricing() {
                if (!this.priced || !this.trip) return {total: 0, deposit: 0, months: 0, installment: 0};
                const cost = Number(this.trip.cost) || 0;
                const airCredit = this.selectedAddons.some(a => a.type === 'aircredit');
                const dep = this.selectedDeparture;
                const paying = !!this.newReg.secondPaying && !!this.newReg.second_id;
                const key = paying ? 'double_cost' : 'single_cost';
                const sum = this.selectedAddons.reduce((s, a) => s + (Number(a[key]) || 0), 0);
                const depCost = (dep && !airCredit) ? (Number(dep[key]) || 0) : 0;

                let total = paying ? cost * 2 + depCost + sum : cost + depCost + sum;
                let down = paying ? 3000 : 1500;
                down = Math.min(total, down);

                // No plan means nothing is owed here — mirrors the server's downAmount=0.
                if (this.noPlan) return {total, deposit: 0, months: 0, installment: 0};

                const entire = !this.isWaitlist && this.newReg.payment === 'entire';
                const deposit = entire ? total : down;

                let months = 0, installment = 0;
                if (!entire && !this.isWaitlist && this.trip.start_on) {
                    const today = new Date();
                    const start = new Date(today.getFullYear(), today.getMonth() + 1, 15);
                    const end = new Date(this.trip.start_on + 'T00:00:00');
                    end.setDate(end.getDate() - (Number(this.trip.deadline) || 0));
                    months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
                    if (end.getDate() < start.getDate()) months--;
                    months += 1;
                    if (months < 1) months = 1;
                    installment = Math.floor(((total - down) / months) * 100) / 100;
                }
                return {total, deposit, months, installment};
            },
            canSubmit() {
                return !!this.newReg.trip_id && !!this.newReg.user_id && !!this.newReg.type && !this.submitting;
            },
        },
        methods: {
            loadTripDetails(force) {
                if (!this.newReg.trip_id) {
                    this.trip = null;
                    this.tripAddons = [];
                    return;
                }
                this.loadingAddons = true;
                Promise.all([
                    http.get('/api/trips/' + this.newReg.trip_id, {force}),
                    http.get('/api/trips/' + this.newReg.trip_id + '/addons/0/500', {force}),
                ]).then(([tripRes, addonRes]) => {
                    this.trip = tripRes.data;
                    this.tripAddons = (addonRes.data && addonRes.data.addons) || [];
                    // Wait-list trips hide the plan selector and always get a deposit-only
                    // plan, so drop any 'none'/'entire' choice made against a previous trip.
                    if (this.trip && this.trip.waitlist) this.newReg.payment = 'monthly';
                }).finally(() => {
                    this.loadingAddons = false;
                });
            },
            submit() {
                this.submitting = true;
                this.$emit('loading', true);

                const payload = {
                    trip_id: this.newReg.trip_id,
                    user_id: this.newReg.user_id,
                    type: this.newReg.type,
                    second_id: this.newReg.second_id || null,
                    second_type: this.newReg.second_id ? this.newReg.second_type : null,
                    secondPaying: !!this.newReg.secondPaying,
                    departureId: this.priced ? this.newReg.departureId : null,
                    addons: this.priced ? this.selectedAddonIds : [],
                    payment: this.newReg.payment,
                };

                http.post('/api/registrations/', payload, {force: true}).then(response => {
                    const created = response.data || {};
                    this.$reloadCard('registrations');
                    this.$saveAlert(false);
                    this.$closeCard(this.card);

                    if (created.id) {
                        this.$openCard('registration', {registrationId: created.id}, this.card);
                    }
                    if (this.chargeNow && created.plan_id) {
                        this.$openCard('make-payment', {planId: created.plan_id, regId: created.id}, this.card);
                    }
                }).catch(err => {
                    const msg = (err.response && err.response.data && err.response.data.message)
                        ? err.response.data.message
                        : 'The registration could not be created.';
                    window.alert(msg);
                    this.submitting = false;
                    this.$emit('loading', false);
                });
            },
        },
        watch: {
            'newReg.trip_id': function() {
                this.newReg.departureId = null;
                this.selectedAddonIds = [];
                this.loadTripDetails();
            },
        },
        mounted() {
            this.$saveAlert(true, 'Are you done creating this new registration? Unsaved changes will be lost.');
        }
    };
</script>

<style lang="scss" scoped>
    .addon-row {
        padding: 2px 0;
    }

    .summary {
        border-top: 1px solid #e7e7e7;
        padding-top: 8px;
    }

    .summary-row {
        display: flex;
        justify-content: space-between;
        padding: 3px 0;
    }
</style>
